import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import dismissIcon from "@assets/facilityNetwork/ba-dismiss.svg";
import chevronWhite from "@assets/facilityNetwork/req-chevron-white.svg";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import { FN } from "./facilityNetworkTheme";

type Props = {
  open: boolean;
  parentName?: string;
  facilityName?: string;
  facilityRole?: "parent" | "child";
  title?: string;
  submitting?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
};

/**
 * Figma node 9132:98962 — Send unlink request dialog
 */
export default function FacilityNetworkLeaveNetworkDialog({
  open,
  parentName,
  facilityName,
  facilityRole = "parent",
  title,
  submitting = false,
  onClose,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState("");
  const targetName = facilityName || parentName || "facility";

  useEffect(() => {
    if (open) {
      setReason("");
    }
  }, [open]);

  const dialogTitle =
    title ||
    (facilityRole === "child"
      ? "Request to unlink child facility"
      : "Request to unlink parent facility");

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 600,
          m: 2,
          borderRadius: "4px",
          boxShadow:
            "0px 8px 16px 0px rgba(0,0,0,0.14), 0px 0px 2px 0px rgba(0,0,0,0.12)",
          bgcolor: "#fff",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pl: "24px",
            pr: "22px",
            pt: "12px",
            pb: "12px",
          }}
        >
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: "#242424",
              lineHeight: "20px",
            }}
          >
            {dialogTitle}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
            size="small"
            sx={{ p: "2px", width: 28, height: 28, borderRadius: "4px" }}
          >
            <FacilityNetworkFigmaIcon src={dismissIcon} size={20} alt="Dismiss" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            borderTop: "1px solid #e5e5e5",
            borderBottom: "1px solid #e5e5e5",
            px: "24px",
            py: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "16px",
              letterSpacing: "0.03px",
              color: "#242424",
            }}
          >
            This will send an unlink request to{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {targetName}.
            </Box>{" "}
            The link remains active until they approve.
          </Typography>

          {/* Pink Alert Callout */}
          <Box
            sx={{
              bgcolor: "#fde7e9",
              border: "1px solid #e7a3a7",
              borderRadius: "4px",
              p: "12px",
              boxShadow:
                "1px 1px 2px rgba(98,102,104,0.05), -1px -1px 2px rgba(98,102,104,0.05)",
            }}
          >
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                fontWeight: 400,
                lineHeight: "16px",
                letterSpacing: "0.03px",
                color: "#605e5c",
              }}
            >
              The other party must approve this request before the link is removed. If they
              don&apos;t respond within 30 days the request expires and the link stays active.
            </Typography>
          </Box>

          {/* Reason Field */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%" }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                color: "#242424",
              }}
            >
              Reason for unlink request{" "}
              <Box component="span" sx={{ color: "#a4262c" }}>
                *
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                fontWeight: 400,
                lineHeight: "16px",
                letterSpacing: "0.03px",
                color: "#605e5c",
                mb: "6px",
              }}
            >
              This note will be shared with the other party to help them decide whether to approve.
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder="Explain why you want to unlink this facility..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fafafa",
                  borderRadius: "5px",
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: "#242424",
                  lineHeight: "16px",
                  p: "8px 12px",
                  "& fieldset": {
                    borderColor: "#e5e5e5",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d4d4d4",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: FN.primary,
                  },
                  "& textarea::placeholder": {
                    color: "#605e5c",
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "8px",
            pt: "8px",
            pb: "24px",
            px: "24px",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{
              height: 32,
              px: "12px",
              py: "6px",
              borderRadius: "4px",
              border: "1px solid #d4d4d4",
              color: "#262626",
              bgcolor: "#fff",
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                borderColor: "#b3b3b3",
                bgcolor: "#f9f9f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onConfirm(reason.trim())}
            disabled={submitting || !reason.trim()}
            sx={{
              height: 32,
              px: "16px",
              py: "8px",
              borderRadius: "4px",
              bgcolor: "#d13438",
              color: "#fff",
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0px 2px 4px rgba(0,0,0,0.25)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              "&:hover": {
                bgcolor: "#a4262c",
              },
              "&.Mui-disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <>
                Send Unlink
                <FacilityNetworkFigmaIcon src={chevronWhite} size={12} alt="" />
              </>
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
